import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatAccordion } from "blocks-nextjs/blocks-react/components/Accordion";
import { CatAccordionPanel } from "blocks-nextjs/blocks-react/components/AccordionPanel";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconArrowDownAlt } from "blocks-nextjs/blocks-react/components/Icons/ArrowDownAlt/ArrowDownAlt";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatStatusIndicator } from "blocks-nextjs/blocks-react/components/StatusIndicator/StatusIndicator";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import IDLEMECCharger from "../../assets/IDLE-MEC-Charger.svg";
import ChargeHistoryTotal from "../../components/ChargeHistoryTotal/ChargeHistoryTotal";
import ChargerLiveView from "../../components/ChargerLiveView/ChargerLiveView";
import ChargerTimeline from "../../components/ChargerTimeline/ChargerTimeline";
import DateRangeDropdown from "../../components/DateRangeDropdown/DateRangeDropdown";
import ChargeHistoryDrawer from "../../components/Drawers/ChargeHistoryDrawer";
import EnergyOutputHistory from "../../components/EnergyOutputHistory/EnergyOutputHistory";
import CustomDatePickerPopover from "../../components/GenerateLink/CustomDatePickerPopover";
import GenerateLink from "../../components/GenerateLink/GenerateLink";
import ChargerHeaderDetails from "../../components/HeaderDetails/ChargerHeaderDetails";
import SharedList from "../../components/SharedList/SharedList";
import { rebootCharger, startCharger, stopCharger } from "../../services/api/eflow-charger-controls.service";
import { getChargerTransactions } from "../../services/api/eflow-charger-dashboard-service";
import { useActiveTransactionIdQuery, useChargerConnectorDataQuery, useChargerConnectorsEnergyHistogramQuery, useChargerDetailsQuery, useChargerHistoryQuery, useChargerTransactionsCountQuery, useChargerTransactionsQuery } from "../../services/query/eflow-charger-dashboard-api.query";
import { hasEnergyFlowLinksViewPermission, hasStrictEnergyFlowLinksViewPermission, isCatAdminUserEnergyFlowLinks } from "../../shared/validateUserPermission";
import useAppConfigStore from "../../store/useAppConfigStore";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import useSharedLinksStore from "../../store/useSharedLinksStore";
import { CommunicationResult } from "../../swagger/caasService-v1";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
import ChargeHistoryDetail from "../Projects/ChargeHistoryDetail";
import Alerts from "./Alerts.component";
import "./AssetPageDetail.css";
import { getAssetImageIcon, getCESTLocalTime, isLastReportedTimeStale, isTimeOnlyTimestamp } from "./ProjectDetails.helper";
/**
 * ChargerPageDetail Component
 *
 * Displays detailed information about a charger including:
 * - Live view of connector statuses
 * - Charger timeline and activity history
 * - Energy consumption metrics
 * - Transaction history
 */
const ChargerPageDetail = ({ isDrawerActive = true, isRefreshComponent, onChargeHistoryStateChange, onGenerateLinkStateChange, showChargeHistory: externalShowChargeHistory, showGenerateLink: externalShowGenerateLink }) => {
    const [isAlertsAvailable, setIsAlertsAvailable] = useState(false);
    const [alertsCountForLast24hrs, setAlertsCountForLast24hrs] = useState(0);
    const [alertsSortOrder, setAlertsSortOrder] = useState("DESC");
    const [showSortMenu, setShowSortMenu] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [prevChargeHistoryDrawerActive, setPrevChargeHistoryDrawerActive] = useState(false);
    const [prevShowChargeHistory, setPrevShowChargeHistory] = useState(false);
    const [prevShowGenerateLink, setPrevShowGenerateLink] = useState(false);
    const [internalShowGenerateLink, setInternalShowGenerateLink] = useState(false);
    const [internalShowChargeHistory, setInternalShowChargeHistory] = useState(false);
    const alertsAccordionRef = useRef(null);
    const sortButtonRef = useRef(null);
    // Close sort menu when clicking outside
    useEffect(() => {
        if (!showSortMenu)
            return;
        const handleClickOutside = (e) => {
            if (sortButtonRef.current && !sortButtonRef.current.contains(e.target)) {
                setShowSortMenu(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [showSortMenu]);
    const [selectedDateRange, setSelectedDateRange] = useState("Last7Days");
    const [customFromDate, setCustomFromDate] = useState(null);
    const [customToDate, setCustomToDate] = useState(null);
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    // Counter to force refetch when the same date range is selected again
    const [dateRangeSelectionCounter, setDateRangeSelectionCounter] = useState(0);
    // Refetch keys:
    // - mainViewRefetchKey: used for charger details, transactions, histogram, and history queries on the main view.
    // - chargeHistoryRefetchKey: used only for the ChargeHistoryDetail component (drawer).
    // - sharedLinksRefetchKey: used for the SharedList component.
    const [mainViewRefetchKey, setMainViewRefetchKey] = useState(0);
    const [sharedLinksRefetchKey, setSharedLinksRefetchKey] = useState(0);
    const [chargeHistoryRefetchKey, setChargeHistoryRefetchKey] = useState(0);
    // Backwards-compatible aliases for existing usages; prefer mainViewRefetchKey going forward.
    const historyRefetchKey = mainViewRefetchKey;
    const setHistoryRefetchKey = setMainViewRefetchKey;
    // Store the previously selected range before opening custom date picker
    const [previousDateRange, setPreviousDateRange] = useState("Last7Days");
    // Pagination state for transactions
    const [allTransactions, setAllTransactions] = useState([]);
    const [nextCursor, setNextCursor] = useState(undefined);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentSkip, setCurrentSkip] = useState(0);
    // Reset to default date range when drawer becomes inactive
    useEffect(() => {
        if (!isDrawerActive) {
            setSelectedDateRange("Last7Days");
            setCustomFromDate(null);
            setCustomToDate(null);
            setShowCustomDatePicker(false);
        }
    }, [isDrawerActive]);
    // Get charge history drawer state from URL parameters
    const chargeHistoryDrawerActive = searchParams.get("ChargeHistory") === "true";
    // Helper function to update charge history drawer state
    const setChargeHistoryDrawerActive = (isActive) => {
        const params = new URLSearchParams(searchParams);
        if (isActive) {
            params.set("ChargeHistory", "true");
        }
        else {
            params.delete("ChargeHistory");
        }
        setSearchParams(params, { replace: true });
    };
    // Use external prop if provided, otherwise use internal state
    const showGenerateLink = externalShowGenerateLink !== undefined ? externalShowGenerateLink : internalShowGenerateLink;
    const setShowGenerateLink = externalShowGenerateLink !== undefined
        ? (value) => onGenerateLinkStateChange?.(value)
        : setInternalShowGenerateLink;
    const showChargeHistory = externalShowChargeHistory !== undefined ? externalShowChargeHistory : internalShowChargeHistory;
    const setShowChargeHistory = externalShowChargeHistory !== undefined
        ? (value) => onChargeHistoryStateChange?.(value)
        : setInternalShowChargeHistory;
    const { primaryPartyNumber, userEntitlements, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyNumber: state.primaryPartyNumber,
        userEntitlements: state.userEntitlements,
        userPartyNumber: state.userPartyNumber
    }));
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { links } = useSharedLinksStore((state) => ({
        links: state.links
    }));
    // Check if any link has "Active" status (case-insensitive comparison for robustness)
    // Only show badge when we have links (not in error state where links are cleared)
    // Status values after SharedList formatting: "Active", "Expired", or "Deactivated"
    // However, we normalize the comparison to handle any casing variations
    const hasActiveLink = links.length > 0 && links.some((link) => link.status?.toLowerCase() === "active");
    const { assetChargeActivityDetailsData, assetDetails, projectMetaData, projectName } = useProjectDetailsStore((state) => ({
        assetChargeActivityDetailsData: state.assetChargeActivityDetailsData,
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData,
        projectName: state.projectDetails?.projectName
    }));
    const { assetId, assetSourceId, timestamp = "" } = assetDetails || {};
    // Fallback: Get assetSourceId from URL if not in store (e.g., after page reload)
    const urlAssetSourceId = searchParams.get("AssetDrawer");
    const effectiveAssetSourceId = assetSourceId || urlAssetSourceId;
    const isQueryEnabled = useMemo(() => {
        // Always enable query if we have effectiveAssetSourceId and drawer is active
        // We need charger details even when transaction drawer is open (to get charger ID for transactions)
        return !!effectiveAssetSourceId && isDrawerActive;
    }, [effectiveAssetSourceId, isDrawerActive]);
    // Fetch charger details using the assetSourceId as chargerId
    // Query is enabled when drawer is active (including when transaction drawer is open)
    // This ensures we have charger details needed for transaction history
    const { data: chargerDetailsData, isError: isChargerDetailsError, isLoading: isChargerDetailsLoading, refetch: refetchChargerDetails } = useChargerDetailsQuery(effectiveAssetSourceId || "", projectMetaData?.projectId || "", isQueryEnabled, historyRefetchKey, !chargeHistoryDrawerActive, // Disable refetch interval when transaction drawer is open
    projectMetaData?.offset || "UTC+00:00");
    const chargerDetails = chargerDetailsData?.chargerDetails;
    // Try to get the UUID to use for charger-specific API calls
    // Priority order based on API flow:
    // 1. assetId (UUID returned from search API - PRIMARY)
    // 2. chargerAssetId (alternative UUID field)
    // 3. assetSourceId (original ID - last resort, may cause 400 Bad Request)
    const effectiveChargerAssetId = chargerDetails?.assetId || chargerDetails?.chargerAssetId || chargerDetails?.assetSourceId;
    const effectivePartyNumber = userPartyNumber || primaryPartyNumber;
    const isChargerDashboardEnabled = appConfig?.release_1_1?.showChargingLive === true;
    const isChargerControlsEnabled = appConfig?.release_1_1?.showChargerControls === true &&
        hasStrictEnergyFlowLinksViewPermission(userEntitlements);
    const getRequiredChargerControlContext = (action) => {
        const missing = [
            !effectiveChargerAssetId ? "charger asset id" : null,
            !projectMetaData?.projectId ? "project id" : null
        ].filter(Boolean);
        if (missing.length > 0) {
            throw new Error(`Unable to ${action} charger: missing required context (${missing.join(", ")})`);
        }
        return {
            assetId: effectiveChargerAssetId,
            projectId: projectMetaData.projectId
        };
    };
    /** Assert a start-transaction response succeeded (CommunicationResult-based). */
    const assertStartSucceeded = (response) => {
        const status = response?.transactionStartDetails?.status;
        if (!status || status === CommunicationResult.APPLIED)
            return;
        if (status === CommunicationResult.PENDING) {
            throw new Error(`Unable to start charger: request is still pending`);
        }
        throw new Error(`Unable to start charger: request returned status ${status}`);
    };
    /** Assert a stop/reboot response succeeded (boolean result-based). */
    const assertBooleanResultSucceeded = (action, response) => {
        // result === undefined means the API omitted it — treat as success for backward compat
        if (response?.result === false) {
            throw new Error(`Unable to ${action} charger: request was not applied`);
        }
    };
    const handleStartCharger = async (connectorId) => {
        const { assetId, projectId } = getRequiredChargerControlContext("start");
        const response = await startCharger({
            assetId,
            connectorId,
            projectId
        });
        assertStartSucceeded(response);
        await refetchChargerDetails();
    };
    // Prefixed because `timezoneOffset` is already taken below.
    const stopLookupTimezoneOffset = projectMetaData?.offset || "UTC+00:00";
    const stopRequestRef = useRef(null);
    const { fetchActiveTransactionId } = useActiveTransactionIdQuery(effectiveChargerAssetId ?? "", projectMetaData?.projectId ?? "", stopLookupTimezoneOffset);
    const handleStopCharger = (connectorId) => {
        if (stopRequestRef.current !== null) {
            return Promise.resolve({ message: "Another stop operation is already in progress.", ok: false });
        }
        const stopRequest = (async () => {
            try {
                const { assetId, projectId } = getRequiredChargerControlContext("stop");
                const transactionId = await fetchActiveTransactionId(connectorId);
                const response = await stopCharger({
                    assetId,
                    projectId,
                    transactionId
                });
                assertBooleanResultSucceeded("stop", response);
                await refetchChargerDetails();
                return { ok: true };
            }
            catch (error) {
                const message = error instanceof Error && error.message ? error.message : "Failed to stop charger";
                return { message, ok: false };
            }
        })();
        stopRequestRef.current = stopRequest.finally(() => {
            stopRequestRef.current = null;
        });
        return stopRequestRef.current;
    };
    const handleRebootCharger = async () => {
        const { assetId, projectId } = getRequiredChargerControlContext("reboot");
        const response = await rebootCharger({
            assetId,
            projectId,
            resetType: "HARD"
        });
        assertBooleanResultSucceeded("reboot", response);
        await refetchChargerDetails();
    };
    const isChargerCharging = chargerDetails?.connectors?.some((item) => item.status?.toLowerCase() === "charging" || item.connectorStatus?.toLowerCase() === "charging") ?? false;
    const { data: connectorDataResponse } = useChargerConnectorDataQuery(projectMetaData?.projectId ?? "", effectiveChargerAssetId ?? "", effectivePartyNumber, isChargerDashboardEnabled &&
        isQueryEnabled &&
        !!effectiveChargerAssetId &&
        !!effectivePartyNumber &&
        isChargerCharging &&
        !showChargeHistory, historyRefetchKey) || {};
    const connectorDataForLiveView = isChargerDashboardEnabled ? connectorDataResponse?.connectorData : undefined;
    const prevDrawerActiveRef = React.useRef(isDrawerActive);
    const hasDrawerOpenedBefore = React.useRef(isDrawerActive); // Track if drawer has ever opened
    const hasChargeHistoryOpenedBefore = React.useRef(false); // Track if charge history has ever been shown
    // Force history and shared links refetch when drawer RE-OPENS (not initial mount or first open)
    useEffect(() => {
        // Only increment refetch keys if drawer was previously closed and is now opening
        // AND drawer has been opened before (prevents initial mount double calls)
        if (isDrawerActive &&
            prevDrawerActiveRef.current === false &&
            hasDrawerOpenedBefore.current &&
            effectiveAssetSourceId) {
            // Force history refetch by changing key
            // Note: chargerDetails query will automatically fetch when isQueryEnabled becomes true
            setHistoryRefetchKey((prev) => prev + 1);
            // Also refetch shared links
            setSharedLinksRefetchKey((prev) => prev + 1);
        }
        // Mark that drawer has been opened at least once
        if (isDrawerActive) {
            hasDrawerOpenedBefore.current = true;
        }
        prevDrawerActiveRef.current = isDrawerActive;
    }, [isDrawerActive, effectiveAssetSourceId]);
    const formatLastReportedTime = useCallback((time) => {
        if (!time)
            return "";
        // Special handling for projects where API returns time-only format (e.g., "18:00:00")
        const isTimeOnlyFormat = isTimeOnlyTimestamp(time);
        if (isTimeOnlyFormat) {
            // Time-only format: Append today's date (using browser's date)
            // Note: For projects with timezone info, timezone abbreviation should be calculated from projectMetaData
            try {
                const now = new Date();
                const month = String(now.getMonth() + 1).padStart(2, "0");
                const day = String(now.getDate()).padStart(2, "0");
                const year = now.getFullYear();
                const timeFormatted = time.substring(0, 5);
                // Use timezone abbreviation if available, otherwise default to CEST
                const timezoneAbbr = projectMetaData?.timezone ? "Local Time" : "CEST";
                return `${month}/${day}/${year}; ${timeFormatted} ${timezoneAbbr}`;
            }
            catch (error) {
                console.error("Time-only timestamp construction error:", error);
                return time.substring(0, 5);
            }
        }
        // When showCestTimezone is TRUE: API sends timestamps without "Z" already in project timezone (CEST)
        // When showCestTimezone is FALSE: API sends UTC timestamps with "Z", convert to CEST
        if (appConfig?.release_1_1?.showCestTimezone && !time.endsWith("Z")) {
            // API sends timestamp already in project timezone (e.g., "2024-01-15T10:00:00" means 10:00 AM project time)
            // Simply parse and format the time components without timezone conversion
            // This displays the same wall-clock time to all users regardless of their browser timezone
            const [datePart, timePart] = time.split("T");
            const [year, month, day] = datePart.split("-");
            const [hour, minute] = timePart.split(":");
            return `${month}/${day}/${year}; ${hour}:${minute} CEST`;
        }
        else {
            // Legacy mode: Convert UTC to CEST
            return getCESTLocalTime(time, "MM/dd/yyyy; HH:mm") + " CEST";
        }
    }, [appConfig?.release_1_1?.showCestTimezone, projectMetaData?.timezone]);
    // Helper function to parse timestamps for Charger Timeline
    const parseActivityTimestamp = useCallback((timestamp) => {
        if (!timestamp)
            return 0;
        // When showCestTimezone is TRUE and timestamp has no "Z":
        // API sends timestamps already in project's local timezone (e.g., "2025-12-09T11:27:00.000" = 11:27 project local time)
        // We need to interpret this as UTC to get consistent millisecond values across all users
        if (appConfig?.release_1_1?.showCestTimezone && !timestamp.endsWith("Z")) {
            try {
                // Treat the timestamp as if it were UTC by appending 'Z'
                // This ensures all users get the same millisecond value for duration calculations
                // Example: "2025-12-09T11:27:00.000" becomes "2025-12-09T11:27:00.000Z"
                const timestampAsUTC = `${timestamp}Z`;
                const parsedDate = new Date(timestampAsUTC);
                if (isNaN(parsedDate.getTime())) {
                    return 0;
                }
                return parsedDate.getTime();
            }
            catch (error) {
                return 0;
            }
        }
        // Legacy mode: timestamp with "Z" is UTC
        return new Date(timestamp).getTime();
    }, [appConfig?.release_1_1?.showCestTimezone]);
    const handleCustomDateApply = useCallback((startDate, endDate) => {
        // Parse the MM/dd/yyyy date strings and set custom dates in project timezone
        if (startDate && endDate) {
            // Helper to create a UTC date representing a specific time in the project timezone
            const createProjectDate = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
                // Create date string in ISO format for the project timezone
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
                // Parse as UTC so getUTC* methods return the correct values
                return new Date(dateStr + "Z");
            };
            // Parse MM/dd/yyyy format (e.g., "01/15/2026")
            const parseMMDDYYYY = (dateStr) => {
                const parts = dateStr.split("/");
                if (parts.length !== 3)
                    return null;
                return {
                    day: parseInt(parts[1], 10),
                    month: parseInt(parts[0], 10) - 1, // Month is 0-indexed
                    year: parseInt(parts[2], 10)
                };
            };
            const startParts = parseMMDDYYYY(startDate);
            const endParts = parseMMDDYYYY(endDate);
            if (startParts && endParts) {
                // Create dates in project timezone using the createProjectDate helper
                // Start date at 00:00:00.000
                const from = createProjectDate(startParts.year, startParts.month, startParts.day, 0, 0, 0, 0);
                // End date at 23:59:59.999
                const to = createProjectDate(endParts.year, endParts.month, endParts.day, 23, 59, 59, 999);
                // Update dates and set range to CustomDate
                setCustomFromDate(from);
                setCustomToDate(to);
                setSelectedDateRange("CustomDate");
                setPreviousDateRange("CustomDate");
            }
        }
        setShowCustomDatePicker(false);
    }, []);
    const handleCustomDateCancel = useCallback(() => {
        setShowCustomDatePicker(false);
        // Revert to the previous date range selection
        setSelectedDateRange(previousDateRange);
    }, [previousDateRange]);
    const handleDateRangeChange = useCallback((value) => {
        // When switching to CustomDate, don't change selectedDateRange yet
        // Just show the picker - the range will be set when Apply is clicked
        if (value === "CustomDate") {
            setPreviousDateRange(selectedDateRange);
            setShowCustomDatePicker(true);
        }
        else {
            // For other ranges, apply immediately
            setSelectedDateRange(value);
            setPreviousDateRange(value);
            setShowCustomDatePicker(false);
            // Increment counter to force refetch even when selecting the same date range
            setDateRangeSelectionCounter((prev) => prev + 1);
        }
    }, [selectedDateRange]);
    // Get timezone offset from project metadata for timezone-aware date formatting
    const timezoneOffset = useMemo(() => {
        return projectMetaData?.offset || "UTC+00:00";
    }, [projectMetaData?.offset]);
    // Calculate date range based on selection
    const { fromDate, toDate } = useMemo(() => {
        // If custom date range is selected and custom dates are set, use them
        if (selectedDateRange === "CustomDate" && customFromDate && customToDate) {
            return { fromDate: customFromDate, toDate: customToDate };
        }
        // Calculate today in the project's timezone to ensure consistency
        const getCurrentDateInTimezone = () => {
            const now = new Date();
            // Extract offset hours from format like "UTC+01:00" or "+09:00"
            const offsetStr = timezoneOffset.replace("UTC", "");
            const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
            if (offsetMatch) {
                const sign = offsetMatch[1] === "+" ? 1 : -1;
                const hours = parseInt(offsetMatch[2], 10);
                const minutes = parseInt(offsetMatch[3] || "0", 10);
                const totalOffsetMinutes = sign * (hours * 60 + minutes);
                // Get UTC time and apply project timezone offset
                const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
                const projectTime = new Date(utcTime + totalOffsetMinutes * 60000);
                return projectTime;
            }
            return now;
        };
        const today = getCurrentDateInTimezone();
        // Create a UTC date representing midnight in the project timezone
        // This ensures when we extract UTC components, we get the correct date
        const createProjectDate = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
            // Create date string in ISO format for the project timezone
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
            // Parse as UTC so getUTC* methods return the correct values
            return new Date(dateStr + "Z");
        };
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const todayDay = today.getDate();
        // Start of today in project timezone (as UTC)
        const fromDateDefault = createProjectDate(todayYear, todayMonth, todayDay, 0, 0, 0, 0);
        // End of today in project timezone (as UTC)
        const toDateDefault = createProjectDate(todayYear, todayMonth, todayDay, 23, 59, 59, 999);
        let fromDate = fromDateDefault;
        let toDate = toDateDefault;
        switch (selectedDateRange) {
            case "Today":
                // Already set correctly
                break;
            case "Yesterday": {
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                fromDate = createProjectDate(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
                toDate = createProjectDate(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
                break;
            }
            case "Last7Days": {
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 6);
                fromDate = createProjectDate(weekAgo.getFullYear(), weekAgo.getMonth(), weekAgo.getDate(), 0, 0, 0, 0);
                break;
            }
            case "Last30Days": {
                const monthAgo = new Date(today);
                monthAgo.setDate(today.getDate() - 29);
                fromDate = createProjectDate(monthAgo.getFullYear(), monthAgo.getMonth(), monthAgo.getDate(), 0, 0, 0, 0);
                break;
            }
            case "CurrentWeek": {
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - today.getDay()); // Sunday
                fromDate = createProjectDate(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), 0, 0, 0, 0);
                // Current week ends today
                break;
            }
            case "PreviousWeek": {
                const currentWeekStart = new Date(today);
                currentWeekStart.setDate(today.getDate() - today.getDay()); // Current week Sunday
                const prevWeekStart = new Date(currentWeekStart);
                prevWeekStart.setDate(currentWeekStart.getDate() - 7); // Previous week Sunday
                const prevWeekEnd = new Date(currentWeekStart);
                prevWeekEnd.setDate(currentWeekStart.getDate() - 1); // Previous week Saturday
                fromDate = createProjectDate(prevWeekStart.getFullYear(), prevWeekStart.getMonth(), prevWeekStart.getDate(), 0, 0, 0, 0);
                toDate = createProjectDate(prevWeekEnd.getFullYear(), prevWeekEnd.getMonth(), prevWeekEnd.getDate(), 23, 59, 59, 999);
                break;
            }
            case "CurrentMonth": {
                fromDate = createProjectDate(todayYear, todayMonth, 1, 0, 0, 0, 0);
                break;
            }
            case "LastMonth": {
                const lastMonthStart = new Date(todayYear, todayMonth - 1, 1);
                const lastMonthEnd = new Date(todayYear, todayMonth, 0); // Last day of previous month
                fromDate = createProjectDate(lastMonthStart.getFullYear(), lastMonthStart.getMonth(), lastMonthStart.getDate(), 0, 0, 0, 0);
                toDate = createProjectDate(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), lastMonthEnd.getDate(), 23, 59, 59, 999);
                break;
            }
            default: {
                // Default to Last7Days
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 6);
                fromDate = createProjectDate(weekAgo.getFullYear(), weekAgo.getMonth(), weekAgo.getDate(), 0, 0, 0, 0);
                break;
            }
        }
        return { fromDate, toDate };
    }, [selectedDateRange, customFromDate, customToDate, timezoneOffset]);
    // Memoize query parameters to prevent unnecessary re-renders and duplicate API calls
    // Format dates with timezone offset for CaaS v1 API
    // Include selectedDateRange to force refetch when user re-selects the same date range
    // Include dateRangeSelectionCounter to force refetch on every date range selection
    const transactionQueryParams = useMemo(() => {
        if (!fromDate || !toDate) {
            return { limit: "20", orderBy: "DESC", skip: "0", sortBy: "startTime" };
        }
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        return {
            fromTime: formattedFromDate,
            limit: "20",
            orderBy: "DESC",
            skip: "0",
            sortBy: "startTime",
            toTime: formattedToDate
        };
    }, [fromDate, toDate, timezoneOffset, selectedDateRange, dateRangeSelectionCounter]);
    // Query parameters for history and histogram calls (without skip and limit)
    // Include selectedDateRange to force refetch when user re-selects the same date range
    // Include dateRangeSelectionCounter to force refetch on every date range selection
    const historyQueryParams = useMemo(() => {
        if (!fromDate || !toDate) {
            return undefined;
        }
        const formattedFromDate = formatDateWithTimezoneOffset(fromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(toDate, timezoneOffset);
        return {
            fromTime: formattedFromDate,
            toTime: formattedToDate
        };
    }, [fromDate, toDate, timezoneOffset, selectedDateRange, dateRangeSelectionCounter]);
    // Combine historyRefetchKey with dateRangeSelectionCounter for query keys
    // This ensures API calls are made even when re-selecting the same date range
    const combinedRefetchKey = historyRefetchKey * 10000 + dateRangeSelectionCounter;
    const transactionQueryEnabled = isQueryEnabled && !!effectiveChargerAssetId && !!fromDate && !!toDate && !showChargeHistory;
    const { data: transactionsData, isLoading: isTransactionsLoading } = useChargerTransactionsQuery(effectiveChargerAssetId ?? "", projectMetaData?.projectId ?? "", transactionQueryParams, transactionQueryEnabled, combinedRefetchKey);
    const histogramQueryEnabled = isQueryEnabled && !!effectiveChargerAssetId && !!fromDate && !!toDate && !showChargeHistory;
    const { data: energyHistogramData, isLoading: isEnergyHistogramLoading } = useChargerConnectorsEnergyHistogramQuery(effectiveChargerAssetId ?? "", projectMetaData?.projectId ?? "", historyQueryParams, histogramQueryEnabled, combinedRefetchKey);
    const historyQueryEnabled = isQueryEnabled && !!effectiveChargerAssetId && !!fromDate && !!toDate && !showChargeHistory;
    const { data: historyData, isLoading: isHistoryLoading } = useChargerHistoryQuery(effectiveChargerAssetId ?? "", projectMetaData?.projectId ?? "", historyQueryParams, historyQueryEnabled, combinedRefetchKey);
    const transactionsCountQueryEnabled = isQueryEnabled && !!effectiveChargerAssetId && !!fromDate && !!toDate && !showChargeHistory;
    const { data: transactionsCountData, isLoading: isTransactionsCountLoading } = useChargerTransactionsCountQuery(effectiveChargerAssetId ?? "", projectMetaData?.projectId ?? "", historyQueryParams, transactionsCountQueryEnabled, combinedRefetchKey);
    useEffect(() => {
        const wasShowingHistory = prevShowChargeHistory;
        const isShowingHistory = showChargeHistory;
        if (!isDrawerActive) {
            setPrevShowChargeHistory(showChargeHistory);
            return;
        }
        if (wasShowingHistory === true && isShowingHistory === false) {
            // Force history refetch by changing key when coming back from charge history
            setHistoryRefetchKey((prev) => prev + 1);
            // Also refetch shared links
            setSharedLinksRefetchKey((prev) => prev + 1);
        }
        else if (wasShowingHistory === false && isShowingHistory === true && hasChargeHistoryOpenedBefore.current) {
            // Force transaction refetch when navigating to charge history view
            // Only increment if charge history has been shown before (prevents initial navigation double calls)
            setChargeHistoryRefetchKey((prev) => prev + 1);
        }
        // Mark that charge history has been shown at least once
        if (isShowingHistory) {
            hasChargeHistoryOpenedBefore.current = true;
        }
        // Update previous value after all conditional logic
        setPrevShowChargeHistory(showChargeHistory);
    }, [showChargeHistory, isDrawerActive]);
    // Legacy support for URL-based charge history drawer
    useEffect(() => {
        if (prevChargeHistoryDrawerActive === true && chargeHistoryDrawerActive === false && isDrawerActive) {
            // Force history refetch by changing key
            setHistoryRefetchKey((prev) => prev + 1);
            // Also refetch shared links
            setSharedLinksRefetchKey((prev) => prev + 1);
        }
        setPrevChargeHistoryDrawerActive(chargeHistoryDrawerActive);
    }, [chargeHistoryDrawerActive, prevChargeHistoryDrawerActive, isDrawerActive]);
    // Refetch shared links when returning from Generate Link view
    useEffect(() => {
        if (prevShowGenerateLink === true && showGenerateLink === false && isDrawerActive) {
            // Force refetch of all APIs when coming back from generate link view
            setHistoryRefetchKey((prev) => prev + 1);
            setSharedLinksRefetchKey((prev) => prev + 1);
        }
        setPrevShowGenerateLink(showGenerateLink);
    }, [showGenerateLink, prevShowGenerateLink, isDrawerActive]);
    // Update allTransactions when transactionsData changes (initial load or date range change)
    useEffect(() => {
        if (transactionsData?.transactions) {
            setAllTransactions(transactionsData.transactions);
            // Get nextCursor from the top-level responseMetadata
            const responseMetadata = transactionsData?.responseMetadata;
            setNextCursor(responseMetadata?.nextCursor);
        }
        else if (transactionsData !== undefined) {
            // If transactionsData exists but transactions is empty/undefined, clear the state
            setAllTransactions([]);
            setNextCursor(undefined);
        }
    }, [transactionsData]);
    // Reset pagination state when date range changes, but keep existing transactions
    useEffect(() => {
        setNextCursor(undefined);
        setCurrentSkip(0);
    }, [fromDate, toDate]);
    // Function to load more transactions
    const handleLoadMoreTransactions = useCallback(async () => {
        if (!nextCursor || !effectiveChargerAssetId || !projectMetaData?.projectId || isLoadingMore)
            return;
        setIsLoadingMore(true);
        try {
            const newSkip = currentSkip + 20;
            const newData = await getChargerTransactions(effectiveChargerAssetId, projectMetaData.projectId, {
                ...transactionQueryParams,
                nextCursor,
                skip: newSkip.toString()
            });
            if (newData?.transactions) {
                // Merge new transactions with existing ones
                setAllTransactions((prev) => [...prev, ...newData.transactions]);
                // Update skip value for next call
                setCurrentSkip(newSkip);
                // Update nextCursor from the top-level responseMetadata
                // Explicitly check if nextCursor exists and is not empty to handle empty responseMetadata: {}
                const responseMetadata = newData
                    ?.responseMetadata;
                const newNextCursor = responseMetadata?.nextCursor;
                // Only set nextCursor if it has a valid value, otherwise set to undefined to disable "Show More"
                setNextCursor(newNextCursor && newNextCursor.trim() !== "" ? newNextCursor : undefined);
            }
            else {
                // If no transactions in response, clear nextCursor to hide "Show More"
                setNextCursor(undefined);
            }
        }
        catch (error) {
            console.error("Error loading more transactions:", error);
        }
        finally {
            setIsLoadingMore(false);
        }
    }, [
        currentSkip,
        effectiveChargerAssetId,
        isLoadingMore,
        nextCursor,
        projectMetaData?.projectId,
        transactionQueryParams
    ]);
    const msToHMS = useCallback((duration) => {
        const minutes = Math.floor((duration / (1000 * 60)) % 60);
        const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
        return parseFloat(`${hours ? hours : "0"}.${minutes ? minutes : "0"}`);
    }, []);
    // Calculate reporting status with timezone-aware staleness check
    const isTimeOnlyFormat = isTimeOnlyTimestamp(timestamp);
    const isNotReporting = !isTimeOnlyFormat && isLastReportedTimeStale(timestamp, projectMetaData?.timeZone || projectMetaData?.timezone);
    const reportingStatus = isNotReporting ? "Not Reporting" : "Reporting";
    // activity is the charger's last reported value and is never cleared when it stops
    // communicating, so it must not be shown as the current status once stale.
    const chargerStatus = isNotReporting ? "Not Reporting" : assetDetails?.activity;
    const legendItems = [
        { color: "#8CBAFF", label: "Idle", value: 0 },
        { color: "#1AC69E", label: "Charging", value: 0 },
        { color: "#FB5A00", label: "Discharging", value: 0 },
        { color: "#777777", label: "Not Reported", value: 0 }
    ];
    const chartValues = {
        Charging: 0,
        Discharging: 0,
        Idle: 0,
        "Not Reported": 0
    };
    const assetTimelineStatus = [];
    const assetChargeActivityDetailsDataFormatted = appConfig?.release_1_1?.showCestTimezone
        ? assetChargeActivityDetailsData
        : assetChargeActivityDetailsData.map((item) => {
            const { activityEndTimeStamp = "", activityStartTimeStamp = "" } = item;
            const todayDate = new Date().toISOString().split("T")[0] + "T";
            if (activityStartTimeStamp && activityStartTimeStamp.indexOf("T") === -1) {
                item.activityStartTimeStamp = todayDate + activityStartTimeStamp;
            }
            if (activityEndTimeStamp && activityEndTimeStamp.indexOf("T") === -1) {
                item.activityEndTimeStamp = todayDate + activityEndTimeStamp;
            }
            return item;
        });
    if (assetChargeActivityDetailsDataFormatted && assetChargeActivityDetailsDataFormatted.length > 0) {
        const assetChargeActivitySorted = assetChargeActivityDetailsDataFormatted.sort((a, b) => {
            return parseActivityTimestamp(b.activityStartTimeStamp) - parseActivityTimestamp(a.activityStartTimeStamp);
        });
        assetChargeActivitySorted.forEach((assetChargeActivity) => {
            const { activity = "", activityEndTimeStamp = "", activityStartTimeStamp = "", endSoc = 0, startSoc = 0 } = assetChargeActivity;
            if (assetChargeActivity.assetId === assetId) {
                const activityStatus = {
                    batteryLevel: `${Math.round(startSoc)}% -> ${Math.round(endSoc)}%`,
                    details: "",
                    time: formatLastReportedTime(activityStartTimeStamp)
                };
                if (activity === "Charging" || activity === "AC_Charging" || activity === "DC_Charging") {
                    activityStatus.details = `Charging started at ${Math.round(startSoc)}%`; // activity timeline
                    chartValues.Charging += Math.abs(parseActivityTimestamp(activityEndTimeStamp) - parseActivityTimestamp(activityStartTimeStamp));
                }
                else if (activity === "Idle") {
                    activityStatus.details = activity; // activity timeline
                    chartValues.Idle += Math.abs(parseActivityTimestamp(activityEndTimeStamp) - parseActivityTimestamp(activityStartTimeStamp));
                }
                else {
                    activityStatus.details = `Charging stopped at ${Math.round(endSoc)}%`; // activity timeline
                    chartValues.Discharging += Math.abs(parseActivityTimestamp(activityEndTimeStamp) - parseActivityTimestamp(activityStartTimeStamp));
                }
                const totalDiffInSoc = Math.abs(startSoc - endSoc);
                const totalDiffInMs = Math.abs(parseActivityTimestamp(activityEndTimeStamp) - parseActivityTimestamp(activityStartTimeStamp));
                // Only show record if total activity is greater than 2 minutes and startsoc and endsoc are not same
                if (totalDiffInSoc > 0 && totalDiffInMs > 120000) {
                    assetTimelineStatus.push(activityStatus);
                }
            }
        });
        let totalHrsRecorder = 0, notReportedIndex = 3;
        legendItems.forEach((legendItem, index) => {
            const totalSeconds = chartValues[legendItem.label];
            legendItem.value = msToHMS(totalSeconds);
            totalHrsRecorder += totalSeconds;
            if (legendItem.label === "Not Reported") {
                notReportedIndex = index;
            }
        });
        const notReportedPowerHrs = (24 - msToHMS(totalHrsRecorder)).toFixed(2);
        legendItems[notReportedIndex].value = Number(notReportedPowerHrs);
    }
    // Chart data for new implementation (old pieChart variables removed as unused)
    useEffect(() => {
        //Wrapping this in setTimeout without any delay helps us finding the shadow-root element
        setTimeout(() => {
            //Finding the shadow-root element to modify the styles of the pagination dropdown
            const alertsAccordionInnerPanel = document
                .querySelector(".alerts-accordion-panel")
                ?.shadowRoot.querySelector(".cat-c-accordion-panel__body-inner");
            alertsAccordionInnerPanel?.setAttribute("style", "padding:0px;");
        });
    });
    const isDataAvailableForAsset = (isDataAvailable) => {
        setIsAlertsAvailable(isDataAvailable);
    };
    const last24hrsAlertsCount = (count) => {
        setAlertsCountForLast24hrs(count);
    };
    useEffect(() => {
        if (!isRefreshComponent) {
            setIsAlertsAvailable(false);
            setAlertsCountForLast24hrs(0);
        }
    }, [isRefreshComponent]);
    const getEnergyChartData = (energyData) => {
        const formattedDate = [];
        const connector1Energy = [];
        const connector2Energy = [];
        // Transform API response format (ConnectorsEnergyResponse)
        if (energyData?.energyHistogram && Array.isArray(energyData.energyHistogram)) {
            energyData.energyHistogram.forEach((dayData) => {
                if (dayData.date && dayData.energyDetails) {
                    // Parse date string directly without timezone conversion
                    // Date comes as "2025-04-23" format (YYYY-MM-DD)
                    const [year, month, day] = dayData.date.split("-").map(Number);
                    const date = new Date(year, month - 1, day); // month is 0-indexed
                    const dateStr = date.toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "short"
                    });
                    formattedDate.push(dateStr);
                    // Extract energy values for each connector
                    const connector1 = dayData.energyDetails.find((e) => e.connectorId === "1");
                    const connector2 = dayData.energyDetails.find((e) => e.connectorId === "2");
                    connector1Energy.push(Math.round(connector1?.value || 0));
                    connector2Energy.push(Math.round(connector2?.value || 0));
                }
            });
        }
        const chartData = [];
        // Connector 1 Energy
        chartData.push({
            hoverinfo: "none",
            hovertemplate: "%{y}",
            marker: { color: "#1AC69E" },
            name: "",
            type: "bar",
            x: formattedDate,
            y: connector1Energy
        });
        // Connector 2 Energy
        chartData.push({
            hoverinfo: "none",
            hovertemplate: "%{y}",
            marker: { color: "#2563eb" },
            name: "",
            type: "bar",
            x: formattedDate,
            y: connector2Energy
        });
        // Responsive height based on screen size
        const getResponsiveHeight = () => {
            const width = window.innerWidth;
            if (width < 640)
                return 300; // mobile
            if (width < 1024)
                return 350; // tablet
            return 450; // desktop
        };
        // Responsive margins based on screen size
        const getResponsiveMargins = () => {
            const width = window.innerWidth;
            if (width < 640) {
                return { b: 60, l: 50, pad: 0, r: 20, t: 10 };
            }
            if (width < 1024) {
                return { b: 60, l: 70, pad: 0, r: 25, t: 10 };
            }
            return { b: 50, l: 100, pad: 0, r: 30, t: 10 };
        };
        return {
            config: { displayModeBar: false, staticPlot: false },
            data: chartData,
            layout: {
                autosize: true,
                barcornerradius: 3,
                height: getResponsiveHeight(),
                margin: getResponsiveMargins(),
                responsive: true,
                showlegend: false,
                textangle: 0,
                xaxis: {
                    automargin: true,
                    tickangle: -45,
                    type: "category"
                },
                yaxis: {
                    automargin: true,
                    ticksuffix: " kWh"
                }
            }
        };
    };
    // Calculate total output from energy histogram data
    const calculateTotalOutput = useCallback((energyData) => {
        let total = 0;
        if (energyData?.energyHistogram && Array.isArray(energyData.energyHistogram)) {
            energyData.energyHistogram.forEach((dayData) => {
                if (dayData.energyDetails) {
                    dayData.energyDetails.forEach((connector) => {
                        total += connector.value || 0;
                    });
                }
            });
        }
        return total > 0 ? `${Math.round(total).toLocaleString()} kWh` : "0 kWh";
    }, []);
    // Use real energy histogram data from API
    // Check if energyHistogramData exists and has data
    const hasEnergyHistogramData = energyHistogramData?.energyHistogram && energyHistogramData.energyHistogram.length > 0;
    const dailyEnergyChartData = hasEnergyHistogramData ? getEnergyChartData(energyHistogramData) : null;
    const totalEnergyOutput = hasEnergyHistogramData ? calculateTotalOutput(energyHistogramData) : "0 kWh";
    // Get transaction count from API
    const transactionCount = transactionsCountData?.count || 0;
    // Calculate uptime from history data
    const uptime = useMemo(() => {
        // Note: TypeScript types define connectorStatusHistoryDetails as an array, but the API actually returns an object
        // Using type assertion to match actual API behavior
        const connectorDetails = historyData?.connectorStatusHistoryDetails;
        const uptimeData = connectorDetails?.uptime;
        if (uptimeData?.value) {
            const totalMinutes = uptimeData.value;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.round(totalMinutes % 60);
            return `${hours} hr ${minutes} min`;
        }
        return "0 hr 0 min";
    }, [historyData]);
    // Notify parent component when showGenerateLink state changes
    useEffect(() => {
        if (onGenerateLinkStateChange) {
            onGenerateLinkStateChange(showGenerateLink);
        }
    }, [showGenerateLink, onGenerateLinkStateChange]);
    // Notify parent component when showChargeHistory state changes
    useEffect(() => {
        if (onChargeHistoryStateChange) {
            onChargeHistoryStateChange(showChargeHistory);
        }
    }, [showChargeHistory, onChargeHistoryStateChange]);
    const handleGenerateLinkClose = () => {
        setShowGenerateLink(false);
    };
    // Define a type for linkData if known, otherwise use unknown
    // interface LinkData { ... }
    const handleCreateLink = (linkData) => {
        console.log("Creating link with data:", linkData);
        // Here you would typically make an API call to create the link
        // Modal should stay open, so we don't call setShowGenerateLink(false)
    };
    if (showGenerateLink) {
        return (_jsx("div", { className: "wrapper", "data-test": "asset-page-details", children: _jsx(GenerateLink, { isOpen: showGenerateLink, onClose: handleGenerateLinkClose, onCreateLink: handleCreateLink, refetchKey: sharedLinksRefetchKey }) }));
    }
    if (showChargeHistory) {
        return (_jsx("div", { className: "wrapper", "data-test": "asset-page-details", children: _jsx(ChargeHistoryDetail, { chargerId: effectiveChargerAssetId, isRefreshComponent: showChargeHistory, refetchKey: chargeHistoryRefetchKey }) }));
    }
    return (_jsxs("div", { className: "wrapper ml-0 mr-0", "data-test": "asset-page-details", children: [_jsx("div", { "data-test": "charger-header-details-wrapper", children: _jsx(ChargerHeaderDetails, { activity: chargerStatus, assetName: assetDetails?.assetName, capacity: assetDetails?.capacity, chargerId: chargerDetails?.assetSourceId, iconUrl: getAssetImageIcon(assetDetails), name: assetDetails?.assetMetadata?.productFamily?.name, projectName: projectName || "Hansweert", reportingStatus: reportingStatus, serialNumber: assetDetails?.assetSerialNumber, siteName: chargerDetails?.siteName, streetAddress: assetDetails?.location?.streetAddress }) }), _jsx("div", { className: "flex w-full px-2 sm:px-4", "data-test": "asset-page-details-source", children: _jsxs("div", { className: "flex flex-col w-full space-y-4 pb-3", children: [_jsx("div", { "data-test": "charger-live-view-wrapper", children: _jsx(ChargerLiveView, { chargerDetails: chargerDetails, connectorData: connectorDataForLiveView, isError: isChargerDetailsError, isLoading: isChargerDetailsLoading, onRebootCharger: handleRebootCharger, onRefresh: () => refetchChargerDetails(), onStartCharger: handleStartCharger, onStopCharger: handleStopCharger, onViewChargeHistory: () => setShowChargeHistory(true), showChargerControls: isChargerControlsEnabled }) }), _jsxs("div", { "data-test": "shareable-access-links-section", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4", "data-test": "shareable-links-header", children: [_jsx(CatHeading, { "data-test": "shareable-links-title", variant: "title-sm", children: "Shareable Access Links" }), isCatAdminUserEnergyFlowLinks(userEntitlements) && (_jsx(CatButton, { "data-test": "generate-shareable-link-button", onClick: () => setShowGenerateLink(true), variant: "primary", children: "Generate Shareable Link" }))] }), hasEnergyFlowLinksViewPermission(userEntitlements) && (_jsxs("div", { className: "border rounded-lg bg-white", "data-test": "links-management-container", children: [_jsxs("div", { className: "p-4", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:items-center gap-2 mb-4", children: [_jsx(CatHeading, { "data-test": "links-management-title", variant: "title-sm", children: "Links Management" }), hasActiveLink && (_jsxs("div", { className: "flex items-center px-3 py-1 bg-[#CCCCCC] rounded-full w-fit", "data-test": "sharing-status-badge", children: [_jsx("span", { className: "inline-block w-2 h-2 bg-green-500 rounded-full mr-2" }), _jsx("span", { className: "text-sm text-black font-medium", children: "Sharing" })] }))] }), _jsx(SharedList, { context: "links-management", isDrawerActive: isDrawerActive, refetchKey: sharedLinksRefetchKey })] }), _jsx("div", { className: "bg-gray-100 rounded mt-0 mb-2 py-3 ml-5 mr-2 text-center", children: _jsx("button", { className: "text-blue-600 text-sm font-medium hover:underline", "data-test": "view-history-button", onClick: () => setShowGenerateLink(true), children: "View History" }) })] }))] }), _jsxs("div", { "data-test": "historical-info-section", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-4", "data-test": "historical-info-header", children: [_jsx(CatHeading, { "data-test": "historical-info-title", variant: "title-sm", children: "Historical Info" }), _jsx("div", { className: "flex items-center gap-4", "data-test": "date-range-dropdown-wrapper", children: _jsx(DateRangeDropdown, { "data-test": "date-range-dropdown", onValueChange: handleDateRangeChange, selectedValue: selectedDateRange }) })] }), _jsx(CustomDatePickerPopover, { "data-test": "custom-date-picker-popover", initialEndDate: customToDate
                                        ? `${String(customToDate.getUTCMonth() + 1).padStart(2, "0")}/${String(customToDate.getUTCDate()).padStart(2, "0")}/${customToDate.getUTCFullYear()}`
                                        : "", initialStartDate: customFromDate
                                        ? `${String(customFromDate.getUTCMonth() + 1).padStart(2, "0")}/${String(customFromDate.getUTCDate()).padStart(2, "0")}/${customFromDate.getUTCFullYear()}`
                                        : "", onApply: handleCustomDateApply, onCancel: handleCustomDateCancel, show: showCustomDatePicker, timezoneOffset: timezoneOffset }), _jsxs("div", { className: "flex flex-col md:flex-row align-center mt-4 justify-center min-h-5 gap-4", "data-test": "metrics-cards", children: [_jsxs("div", { className: "flex-1 border-solid border-2 rounded p-4", "data-test": "total-energy-output-card", children: [_jsx("div", { className: "flex items-center", "data-test": "asset-page-details-machine", children: _jsx("span", { className: "font-bold", children: "Total Energy Output" }) }), _jsx("div", { className: "inline-block font-bold my-4 pb-2 border-b-[3px] border-[#FFCD11]", "data-test": "asset-page-details-hours", children: _jsx("span", { className: "text-2xl", "data-test": "total-energy-output-value", children: totalEnergyOutput }) })] }), _jsxs("div", { className: "flex-1 border-solid border-2 rounded p-4", "data-test": "transaction-count-card", children: [_jsx("div", { className: "flex items-center", "data-test": "asset-page-details-machine", children: _jsx("span", { className: "font-bold", children: "Transaction count" }) }), _jsx("div", { className: "inline-block font-bold my-4 pb-2 border-b-[3px] border-[#FFCD11]", "data-test": "asset-page-details-hours", children: isTransactionsCountLoading ? (_jsx(CatLoadingIndicator, { "data-test": "transaction-count-loading" })) : (_jsx("span", { className: "text-2xl", "data-test": "transaction-count-value", children: transactionCount })) })] }), _jsxs("div", { className: "flex-1 border-solid border-2 rounded p-4", "data-test": "uptime-card", children: [_jsx("div", { className: "flex items-center", "data-test": "asset-page-details-machine", children: _jsx("span", { className: "font-bold", children: "Uptime" }) }), _jsx("div", { className: "inline-block font-bold my-4 pb-2 border-b-[3px] border-[#FFCD11]", "data-test": "asset-page-details-hours", children: isHistoryLoading ? (_jsx(CatLoadingIndicator, { "data-test": "uptime-loading" })) : (_jsxs("div", { className: "flex items-center", "data-test": "asset-page-details-machine", children: [_jsx("img", { alt: "", src: IDLEMECCharger }), _jsx("span", { className: "text-2xl", "data-test": "uptime-value", children: uptime })] })) })] })] })] }), _jsxs("div", { className: "flex flex-col lg:flex-row lg:items-stretch gap-4", "data-test": "timeline-and-history-section", children: [_jsxs("div", { className: "w-full lg:w-1/2 border-solid border-2 rounded pt-4 pl-4 pb-0 flex flex-col", "data-test": "timeline-container", children: [_jsx("div", { className: "flex items-center table-title", "data-test": "asset-page-details-header", slot: "header", children: _jsx(CatHeading, { "data-test": "asset-page-details-timeline", variant: "title", children: "Timeline" }) }), _jsx(_Fragment, { children: isTransactionsLoading ? (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", "data-test": "timeline-loading", children: _jsx(CatLoadingIndicator, {}) })) : allTransactions && allTransactions.length > 0 ? (_jsx("div", { className: "relative", "data-test": "timeline-content", children: _jsx(ChargerTimeline, { hasMore: !!nextCursor, isLoadingMore: isLoadingMore, onLoadMore: handleLoadMoreTransactions, timezone: projectMetaData?.timezone, transactions: allTransactions }) })) : (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", "data-test": "timeline-no-data", children: "No data" })) })] }), _jsx("div", { className: "w-full lg:w-1/2 flex flex-col", "data-test": "charge-history-total-container", children: _jsx(ChargeHistoryTotal, { assetId: effectiveChargerAssetId, fromDate: fromDate, projectId: projectMetaData?.projectId, toDate: toDate }, historyRefetchKey) })] }), _jsx("div", { "data-test": "energy-output-history-wrapper", children: _jsx(EnergyOutputHistory, { dailyEnergyChartData: dailyEnergyChartData, fromDate: fromDate
                                    ? `${fromDate.getUTCFullYear()}-${String(fromDate.getUTCMonth() + 1).padStart(2, "0")}-${String(fromDate.getUTCDate()).padStart(2, "0")}`
                                    : undefined, isLoading: isEnergyHistogramLoading, toDate: toDate
                                    ? `${toDate.getUTCFullYear()}-${String(toDate.getUTCMonth() + 1).padStart(2, "0")}-${String(toDate.getUTCDate()).padStart(2, "0")}`
                                    : undefined, totalOutput: totalEnergyOutput }) }), appConfig?.release_1_1?.showMecCharger && assetId && (_jsx("div", { className: "border-solid border-2 rounded min-h-max", children: _jsx(CatAccordion, { "data-test": "MecCharger-accordion", expandAll: isAlertsAvailable, onBlAccordionClose: () => setIsAlertsAvailable(false), onBlAccordionOpen: () => setIsAlertsAvailable(true), ref: alertsAccordionRef, children: _jsxs(CatAccordionPanel, { className: "alerts-accordion-panel", children: [_jsx("div", { slot: "header", children: _jsxs("div", { className: "flex items-center pb-[2px]", children: [_jsx("div", { className: "accordionContainer font-bold text-lg mr-[10px]", children: "Alerts" }), _jsx(CatStatusIndicator, { className: "new-alerts", text: `${alertsCountForLast24hrs} New (24h)`, variant: "warning" })] }) }), _jsxs("div", { className: "flex items-center justify-center gap-[4px] mr-[50px] h-[20px] text-sm font-[550] text-[#3F3F3F] tracking-[0] cursor-pointer", "data-test": "sort-toggle", onClickCapture: (e) => {
                                                e.stopPropagation();
                                                setShowSortMenu((prev) => !prev);
                                            }, ref: sortButtonRef, slot: "after", children: [_jsx(CatIconArrowDownAlt, { className: "w-[16px] h-[16px] flex-none", "data-test": "sort-icon" }), _jsx("span", { "data-test": "sort-text", children: "Sort" })] }), showSortMenu &&
                                            sortButtonRef.current &&
                                            ReactDOM.createPortal(_jsxs("div", { className: "fixed bg-white border border-[#CCCCCC] rounded shadow-lg z-[9999] min-w-[160px]", "data-test": "sort-dropdown", style: {
                                                    left: sortButtonRef.current.getBoundingClientRect().right - 160,
                                                    top: sortButtonRef.current.getBoundingClientRect().bottom + 4
                                                }, children: [_jsx("div", { className: `px-4 py-2 cursor-pointer hover:bg-[#F0F0F0] text-sm ${alertsSortOrder === "DESC" ? "font-bold" : ""}`, "data-test": "sort-by-newest", onClick: () => {
                                                            setAlertsSortOrder("DESC");
                                                            setShowSortMenu(false);
                                                        }, children: "Sort by Newest" }), _jsx("div", { className: `px-4 py-2 cursor-pointer hover:bg-[#F0F0F0] text-sm ${alertsSortOrder === "ASC" ? "font-bold" : ""}`, "data-test": "sort-by-earliest", onClick: () => {
                                                            setAlertsSortOrder("ASC");
                                                            setShowSortMenu(false);
                                                        }, children: "Sort by Earliest" })] }), document.body), _jsx("div", { className: "min-h-max p-4", children: _jsx(Alerts, { assetId: assetId, assetSourceType: "charger", chargerFromDate: fromDate
                                                    ? formatDateWithTimezoneOffset(fromDate, timezoneOffset)
                                                    : undefined, chargerToDate: toDate
                                                    ? formatDateWithTimezoneOffset(toDate, timezoneOffset)
                                                    : undefined, isDataAvailableForAsset: isDataAvailableForAsset, isRefreshComponent: isRefreshComponent, last24hrsAlertsCount: last24hrsAlertsCount, offset: projectMetaData?.offset, onSortOrderChange: setAlertsSortOrder, projectId: projectMetaData?.projectId, projectName: projectMetaData?.projectName, refetchKey: combinedRefetchKey, sortOrder: alertsSortOrder, timezone: projectMetaData?.timezone }) })] }) }) }))] }) }), appConfig?.release_1_1?.showChargerDashboard && (_jsx(ChargeHistoryDrawer, { chargeHistoryDrawerActive: chargeHistoryDrawerActive, chargerId: effectiveChargerAssetId, setChargeHistoryDrawerActive: setChargeHistoryDrawerActive }, `charge-history-drawer-${chargeHistoryDrawerActive ? "open" : "closed"}`))] }));
};
export default ChargerPageDetail;
